import type { InfoMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeInfoPanelAccordionSectionDescriptor } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-accordion";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  SentenceRow,
  useSentenceDisplayHeaderActions,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.sentences._components.sentence-row";
import { useSentenceExamplesQueryOptions } from "@-/dev.improve.haniverse.lib.sentence";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { type ReactNode } from "react";
import { View } from "@-/com.npmjs.react-native";
import { hasLength } from "@-/com.npmjs.ts-array-length";

export function useMorphemeInfoPanelSentencesExamplesSection({
  infoBackCardSlice,
}: {
  infoBackCardSlice: InfoMorphemeSlice;
}): MorphemeInfoPanelAccordionSectionDescriptor {
  const headerActions = useSentenceDisplayHeaderActions();
  const front =
    infoBackCardSlice.morphemeType === "grapheme"
      ? infoBackCardSlice.grapheme
      : infoBackCardSlice.word;
  const {
    data: sentences,
    error,
    isLoading,
  } = useQuery({
    ...useSentenceExamplesQueryOptions(front),
    enabled: infoBackCardSlice.morphemeType === "word",
  });

  let body: ReactNode;
  if (isLoading) {
    // While sentences are loading, show a spinner in the accordion header (via `isLoading` below)
    // rather than surfacing loading copy in the body.
    body = null;
  } else if (error) {
    body = (
      <View className="px-4 py-3">
        <Text className="text-sm text-destructive">Failed to load sentences: {error.message}</Text>
      </View>
    );
  } else if (!sentences || hasLength(sentences, 0)) {
    body = (
      <View className="px-4 py-3">
        <Text className="text-sm text-muted-foreground">No sentence examples for {front} yet.</Text>
      </View>
    );
  } else {
    body = sentences.map((sentence, index) => (
      <SentenceRow key={sentence.id} sentence={sentence} showTopDivider={index > 0} />
    ));
  }

  return {
    bodyKey: "morpheme-info-sentences-examples-body",
    headerKey: "morpheme-info-sentences-examples-header",
    // The header's pinyin/translation defaults replace its chevron, so it never collapses.
    headerActions,
    iconName: "LuMessageSquare",
    // While sentences are loading, show a spinner in place of the chevron rather than surfacing
    // loading copy in the body (matches the compounds section).
    isLoading,
    isOpen: true,
    onToggle: () => {},
    renderBody: () => <View>{body}</View>,
    title: "Examples",
  };
}
